import { defineStore } from 'pinia';
import type {
  FramegrabberCloseStep,
  LensCartCloseStep,
  LensCloseStep,
  ProductFinderAttribution,
  ProductFinderAttributionOptions,
  ProductFinderEntry,
  ProductFinderType
} from '~/utils/productFinderAnalytics/types';
import { getProductFinderItemListMeta } from '~/utils/productFinderAnalytics/helpers';

type CloseStep = LensCloseStep | LensCartCloseStep | FramegrabberCloseStep;

export const useProductFinderAnalyticsStore = defineStore(
  'productFinderAnalytics',
  () => {
    const sessionId = ref<string | null>(null);
    const sessionStartedAt = ref<number | null>(null);
    const finder = ref<ProductFinderType | null>(null);
    const entry = ref<ProductFinderEntry | null>(null);
    const cameraSku = ref<string | null>(null);
    const cartId = ref<string | null>(null);
    const hadRecommendations = ref(false);
    const selectedAccessory = ref(false);
    const calculationIndex = ref(0);
    const closeStep = ref<CloseStep>('method_selection');
    const closeReason = ref<'selection' | 'manual'>('manual');

    const attributionBySku = ref<Record<string, ProductFinderAttribution>>({});

    function startSession(params: {
      finder: ProductFinderType;
      entry: ProductFinderEntry;
      sessionId: string;
      cartId?: string;
      cameraSku?: string;
    }): void {
      sessionId.value = params.sessionId;
      sessionStartedAt.value = Date.now();
      finder.value = params.finder;
      entry.value = params.entry;
      cartId.value = params.cartId ?? null;
      cameraSku.value = params.cameraSku ?? null;
      hadRecommendations.value = false;
      selectedAccessory.value = false;
      calculationIndex.value = 0;
      closeReason.value = 'manual';
      closeStep.value =
        params.entry === 'cart' && !params.cameraSku
          ? 'camera_selection'
          : params.finder === 'framegrabber'
            ? 'before_load'
            : 'method_selection';
    }

    function resetSession(): void {
      sessionId.value = null;
      sessionStartedAt.value = null;
      finder.value = null;
      entry.value = null;
      cameraSku.value = null;
      cartId.value = null;
      hadRecommendations.value = false;
      selectedAccessory.value = false;
      calculationIndex.value = 0;
      closeStep.value = 'method_selection';
      closeReason.value = 'manual';
    }

    function setCameraSku(sku: string): void {
      cameraSku.value = sku;
    }

    function setCloseStep(step: CloseStep): void {
      closeStep.value = step;
    }

    function markRecommendationsReady(): void {
      hadRecommendations.value = true;
      calculationIndex.value += 1;
    }

    function markAccessorySelected(): void {
      selectedAccessory.value = true;
      closeReason.value = 'selection';
    }

    function registerAttribution(
      sku: string,
      options: ProductFinderAttributionOptions
    ): void {
      const listMeta = getProductFinderItemListMeta(options.finder);
      attributionBySku.value = {
        ...attributionBySku.value,
        [sku]: {
          finder: options.finder,
          finder_entry: options.finder_entry,
          finder_session_id: options.finder_session_id,
          finder_camera_sku: options.finder_camera_sku,
          item_list_id: listMeta.item_list_id,
          item_list_name: listMeta.item_list_name,
          selected_via_finder: true
        }
      };
    }

    function getAttribution(sku: string): ProductFinderAttribution | undefined {
      return attributionBySku.value[sku];
    }

    function hasFinderAttributionInCart(skus: string[]): boolean {
      return skus.some((sku) => attributionBySku.value[sku] != null);
    }

    function getTimeInToolMs(): number | undefined {
      if (sessionStartedAt.value == null) {
        return undefined;
      }

      return Date.now() - sessionStartedAt.value;
    }

    return {
      sessionId: computed(() => sessionId.value),
      sessionStartedAt: computed(() => sessionStartedAt.value),
      finder: computed(() => finder.value),
      entry: computed(() => entry.value),
      cameraSku: computed(() => cameraSku.value),
      cartId: computed(() => cartId.value),
      hadRecommendations: computed(() => hadRecommendations.value),
      selectedAccessory: computed(() => selectedAccessory.value),
      calculationIndex: computed(() => calculationIndex.value),
      closeStep: computed(() => closeStep.value),
      closeReason: computed(() => closeReason.value),
      attributionBySku: computed(() => attributionBySku.value),
      startSession,
      resetSession,
      setCameraSku,
      setCloseStep,
      markRecommendationsReady,
      markAccessorySelected,
      registerAttribution,
      getAttribution,
      hasFinderAttributionInCart,
      getTimeInToolMs
    };
  }
);
